import React, { useMemo, useState } from "react";
import { useGlobalData } from "../hooks/useGlobalHooks";
import { OptimizedNumberCell, LiveLTPCell } from "./OptimizedCell";
import CancelConfirmationModal from "./CancelConfirmationModal";
import AlpacaExitSuccessModal from "./AlpacaExitSuccessModal";
import { AlpacaBulkModifyModal } from "./AlpacaBulkModifyModal";
import apiClient from "../utils/apiClient";
import server from "../utils/serverConfig";
import { X, Pencil } from "lucide-react";
import { toast } from "react-hot-toast";

export function AlpacaPositionComparisonModal({
    isOpen,
    onClose,
    clientData,
    positions = [],
    symbolStrategyBaseQty = {},
    orders = []
}) {
    const { users } = useGlobalData();

    // Selection & Modify State
    const [selectedOrderIds, setSelectedOrderIds] = useState(new Set());
    const [modifyModalOpen, setModifyModalOpen] = useState(false);
    const [ordersToModify, setOrdersToModify] = useState([]);

    // Cancel State
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [successModalOpen, setSuccessModalOpen] = useState(false);
    const [successModalActionType, setSuccessModalActionType] = useState("Cancel");
    const [ordersToCancel, setOrdersToCancel] = useState([]);
    const [submittedOrders, setSubmittedOrders] = useState([]);
    const [isCancelling, setIsCancelling] = useState(false);

    // Filter state
    const [selectedAsset, setSelectedAsset] = useState("ALL");

    // Extract underlying prefix
    function getAlpacaUnderlying(symbol) {
        if (!symbol) return "";
        const s = symbol.toUpperCase().trim();
        const match = s.match(/^([A-Z]+)\d/);
        return match ? match[1] : s;
    }

    // Extract strike
    function extractStrike(symbol) {
        if (!symbol) return "N/A";
        const match = symbol.match(/(\d{3,6})(CE|PE|C|P)$/);
        return match ? match[1] : "N/A";
    }

    // Normalize Strategy
    function normalizeStrategy(strat) {
        if (!strat) return "NO_STRATEGY";
        const s = String(strat).trim().toUpperCase();
        if (s === "NO_STRATEGY" || s === "N/A" || s === "NONE" || s === "" || s === "SIMPLE" || s === "N/A STRATEGY") {
            return "NO_STRATEGY";
        }
        return s;
    }

    // Process positions & match orders
    const positionsBySymbolStrategy = useMemo(() => {
        if (!positions || !clientData?.clientId || !symbolStrategyBaseQty) return { rows: [], matchedOrderIds: new Set() };

        const alpacaUsers = (users || []).filter(u => u && String(u.broker).toLowerCase() === "alpaca");
        const alpacaUserIds = new Set(alpacaUsers.map(u => String(u.client_id).toLowerCase()));

        const rows = [];
        const processedKeys = new Set();
        const matchedOrderIds = new Set();

        const clientPositions = positions.filter(p => {
            if (!p) return false;
            const cId = p.clientId || p.client_id;
            if (String(cId).toLowerCase() !== String(clientData.clientId).toLowerCase()) return false;
            
            const isAlpacaBroker = String(p.broker || "").toLowerCase() === "alpaca";
            const isAlpacaUser = alpacaUserIds.has(String(cId).toLowerCase());
            return isAlpacaBroker || isAlpacaUser;
        });

        // Loop over positions
        clientPositions.forEach(pos => {
            const strategy = normalizeStrategy(pos.strategy);
            const lookupKey = `${pos.symbol.toUpperCase().trim()}_${strategy}`;

            if (processedKeys.has(lookupKey)) return;
            processedKeys.add(lookupKey);

            const baseQtyInfo = symbolStrategyBaseQty[lookupKey];
            const baseQty = baseQtyInfo ? baseQtyInfo.baseQty : 0;

            const user = users?.find(u => String(u.client_id).toLowerCase() === String(clientData.clientId).toLowerCase());
            const multiplier = user ? parseFloat(user.multiplier || 1) : 1;
            const expectedQty = multiplier * baseQty;

            // Sum actual netQty for this client
            let actualQty = 0;
            let netSignedQty = 0;
            let pl = 0;
            let avgEntry = 0;
            let ltp = 0;

            clientPositions.forEach(p => {
                const pKey = `${p.symbol.toUpperCase().trim()}_${normalizeStrategy(p.strategy)}`;
                if (pKey === lookupKey) {
                    const netQty = parseFloat(p.netQty ?? p.net_qty ?? p.qty ?? 0);
                    actualQty += Math.abs(netQty);
                    netSignedQty += netQty;
                    pl += parseFloat(p.pl ?? p.pnl ?? 0);
                    avgEntry = parseFloat(p.netAvg ?? p.net_avg ?? avgEntry);
                    ltp = parseFloat(p.ltp ?? ltp);
                }
            });

            // Gather working SL orders matching this position side (opposite to pos)
            const matchedOrders = orders.filter(o => {
                if (!o || String(o.broker || "").toLowerCase() !== "alpaca") return false;
                const cId = o.clientId || o.client_id;
                if (String(cId).toLowerCase() !== String(clientData.clientId).toLowerCase()) return false;

                const statusStr = String(o.status || "").toLowerCase();
                const statusNo = o.status_no !== undefined ? parseInt(o.status_no, 10) : undefined;
                const isPending = ["new", "partially_filled", "accepted", "pending_new", "held", "pending_replace", "pending", "open", "not sent"].includes(statusStr) || statusNo === 6;
                if (!isPending) return false;

                // Normalize Symbol
                const cleanPosSymbol = (pos.symbol || "").toUpperCase().trim();
                const cleanOrderSymbol = (o.symbol || "").toUpperCase().trim();
                if (cleanPosSymbol !== cleanOrderSymbol) return false;

                // Strategy match
                const currentStrategy = normalizeStrategy(o.strategy);
                if (currentStrategy !== strategy) return false;

                const sideStr = String(o.side || "").toLowerCase();
                const isBuy = sideStr === "buy" || sideStr === "1";
                const isSell = sideStr === "sell" || sideStr === "2" || sideStr === "-1";

                const isOpposite = (netSignedQty > 0 && isSell) || (netSignedQty < 0 && isBuy);
                return isOpposite;
            });

            const totalSlQty = matchedOrders.reduce((sum, o) => sum + Math.abs(parseFloat(o.qty || 0)), 0);
            matchedOrders.forEach(o => matchedOrderIds.add(o.order_id || o.orderId));

            rows.push({
                symbol: pos.symbol,
                strategy: strategy === "NO_STRATEGY" ? "N/A" : strategy,
                index: getAlpacaUnderlying(pos.symbol),
                baseQty,
                multiplier,
                expectedQty,
                actualQty,
                netSignedQty,
                pl,
                avgEntry,
                ltp,
                totalSlQty,
                matchingOrders: matchedOrders,
                match: actualQty === expectedQty,
                strike: extractStrike(pos.symbol)
            });
        });

        // Add Orphan SL orders
        const clientOrders = orders.filter(o => {
            if (!o || String(o.broker || "").toLowerCase() !== "alpaca") return false;
            const cId = o.clientId || o.client_id;
            return String(cId).toLowerCase() === String(clientData.clientId).toLowerCase();
        });

        clientOrders.forEach(o => {
            const statusStr = String(o.status || "").toLowerCase();
            const statusNo = o.status_no !== undefined ? parseInt(o.status_no, 10) : undefined;
            const isPending = ["new", "partially_filled", "accepted", "pending_new", "held", "pending_replace", "pending", "open", "not sent"].includes(statusStr) || statusNo === 6;
            if (!isPending) return;

            const orderId = o.order_id || o.orderId;
            if (matchedOrderIds.has(orderId)) return;

            // Only sell side leg is considered a position closing/SL order (matching Optimus)
            const sideStr = String(o.side || "").toLowerCase();
            const isSell = sideStr === "sell" || sideStr === "2" || sideStr === "-1";
            if (!isSell) return;

            // Is an orphan SL order
            rows.push({
                symbol: o.symbol,
                strategy: normalizeStrategy(o.strategy) === "NO_STRATEGY" ? "N/A" : normalizeStrategy(o.strategy),
                index: getAlpacaUnderlying(o.symbol),
                baseQty: 0,
                multiplier: null,
                expectedQty: 0,
                actualQty: 0,
                pl: 0,
                avgEntry: 0,
                ltp: 0,
                totalSlQty: Math.abs(parseFloat(o.qty || 0)),
                matchingOrders: [o],
                match: false,
                strike: extractStrike(o.symbol),
                isOrphan: true
            });
        });

        return { rows, matchedOrderIds };
    }, [positions, clientData, symbolStrategyBaseQty, orders, users]);

    const uniqueAssets = useMemo(() => {
        const rows = positionsBySymbolStrategy.rows || [];
        const assets = new Set(rows.map(r => r.index).filter(Boolean));
        return ["ALL", ...Array.from(assets).sort()];
    }, [positionsBySymbolStrategy]);

    const filteredRows = useMemo(() => {
        const rows = positionsBySymbolStrategy.rows || [];
        if (selectedAsset === "ALL") return rows;
        return rows.filter(r => r.index === selectedAsset);
    }, [positionsBySymbolStrategy, selectedAsset]);

    const toggleOrderSelection = (orderId) => {
        setSelectedOrderIds(prev => {
            const next = new Set(prev);
            if (next.has(orderId)) next.delete(orderId);
            else next.add(orderId);
            return next;
        });
    };

    const handleBulkModify = () => {
        const selectedOrders = [];
        filteredRows.forEach(row => {
            if (row.matchingOrders) {
                row.matchingOrders.forEach(o => {
                    const id = o.order_id || o.orderId;
                    if (selectedOrderIds.has(id)) {
                        selectedOrders.push(o);
                    }
                });
            }
        });

        if (selectedOrders.length === 0) {
            toast.error("Please select at least one order to modify.");
            return;
        }

        setOrdersToModify(selectedOrders);
        setModifyModalOpen(true);
    };

    const handleCancelSelected = () => {
        const toCancel = [];
        filteredRows.forEach(row => {
            if (row.matchingOrders) {
                row.matchingOrders.forEach(o => {
                    const id = o.order_id || o.orderId;
                    if (selectedOrderIds.has(id)) {
                        toCancel.push(o);
                    }
                });
            }
        });

        if (toCancel.length === 0) {
            toast.error("Please select orders to cancel.");
            return;
        }

        setOrdersToCancel(toCancel);
        setSuccessModalActionType("Cancel");
        setConfirmModalOpen(true);
    };

    const handleCancelSingle = (order) => {
        setOrdersToCancel([order]);
        setSuccessModalActionType("Cancel");
        setConfirmModalOpen(true);
    };

    const handleConfirmCancel = async () => {
        if (!ordersToCancel.length) return;
        setIsCancelling(true);
        try {
            const payloads = ordersToCancel.map(o => ({
                orderid: String(o.orderId || o.order_id),
                clientid: clientData.clientId,
                broker: "alpaca"
            }));

            const response = await apiClient.post(`${server.godseye.baseUrl}manual_cancel_orders`, payloads);
            if (response.status === 200) {
                const rawOrders = response.data?.data || response.data?.orders || payloads;
                const responseOrders = rawOrders.map(o => {
                    const match = ordersToCancel.find(c => String(c.orderId || c.order_id) === String(o.orderid || o.order_id || o.orderId));
                    const isSuccess = o.status === "success" || o.status === "SUCCESS";
                    const isFail = o.status === "failed" || o.status === "FAILED" || o.status === "rejected";
                    return {
                        ...o,
                        orderId: o.orderid || o.order_id || o.orderId,
                        clientId: o.clientid || o.client_id || clientData?.clientId,
                        status: isFail ? "Rejected" : (isSuccess ? "CANCELED" : (o.status || "CANCELED")),
                        broker: o.broker || "alpaca",
                        symbol: match?.symbol || ordersToCancel[0]?.symbol || "N/A",
                        rejectReason: o.message || o.response?.message || (isFail ? "Cancellation failed" : "")
                    };
                });
                setSuccessModalActionType("Cancel");
                setSubmittedOrders(responseOrders);
                toast.success("Alpaca SL order cancellation processed!");
                setConfirmModalOpen(false);
                setSuccessModalOpen(true);
            } else {
                toast.error(response.data?.message || "Failed to cancel orders");
            }
        } catch (error) {
            console.error("Error cancelling Alpaca SL orders:", error);
            toast.error("Error cancelling orders");
        } finally {
            setIsCancelling(false);
        }
    };

    const handleRemoveFromBatch = (orderId) => {
        setOrdersToCancel(prev => {
            const next = prev.filter(o => String(o.order_id || o.orderId) !== String(orderId));
            if (next.length === 0) setConfirmModalOpen(false);
            return next;
        });
    };

    const formatNumber = (num, minDec = 2, maxDec = 2) => {
        if (num === undefined || num === null || num === 0) return "-";
        return num.toLocaleString("en-US", { minimumFractionDigits: minDec, maximumFractionDigits: maxDec });
    };

    const formatOrderTime = (dtStr) => {
        if (!dtStr || dtStr === "N/A") return "-";
        try {
            const date = new Date(dtStr);
            return date.toLocaleTimeString("en-US", { hour12: false });
        } catch {
            return dtStr;
        }
    };

    const handleSingleModify = (order) => {
        setOrdersToModify([order]);
        setModifyModalOpen(true);
    };

    if (!isOpen || !clientData) return null;

    return (
        <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-[60] backdrop-blur-sm p-4 animate-fade-in text-xs">
            <div className="bg-[#1a1d29] rounded-xl border border-gray-700 w-full max-w-7xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-700/50 flex justify-between items-center bg-[#14161f]">
                    <div className="flex items-center gap-4">
                        <h2 className="text-white text-base font-bold flex items-center gap-2">
                            <span className="w-2.5 h-2.5 rounded-full bg-[#FCD72B]"></span>
                            Position Comparison: {clientData.clientName} ({clientData.clientId})
                        </h2>
                        {selectedOrderIds.size > 0 && (
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={handleBulkModify}
                                    className="bg-blue-600 hover:bg-blue-500 text-white text-[10px] font-bold px-2.5 py-1 rounded shadow transition-all flex items-center gap-1"
                                >
                                    <Pencil className="w-3 h-3" /> Modify Selected ({selectedOrderIds.size})
                                </button>
                                <button
                                    onClick={handleCancelSelected}
                                    className="bg-red-900/30 hover:bg-red-800/40 text-red-400 text-[10px] font-bold px-2.5 py-1 rounded border border-red-800/30 transition-all"
                                >
                                    Cancel Selected ({selectedOrderIds.size})
                                </button>
                            </div>
                        )}
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-800 rounded-full text-gray-400 transition-colors">
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-auto">
                    {filteredRows.length === 0 ? (
                        <div className="text-center py-12 px-6">
                            <p className="text-gray-500 font-medium">No open positions or working SL orders found for comparison</p>
                        </div>
                    ) : (
                        <div className="bg-[#14161f] overflow-hidden">
                            <div className="overflow-auto max-h-[calc(90vh-120px)]">
                                <table className="w-full text-left border-collapse">
                                    <thead className="bg-[#0f1117] sticky top-0 z-10 font-bold uppercase tracking-wider text-[10px] text-gray-400">
                                        <tr className="border-b border-gray-700/50">
                                            <th colSpan="11" className="text-center py-2 bg-[#14161f] text-gray-400 border-r border-gray-700/50">
                                                Position Comparison
                                            </th>
                                            <th colSpan="7" className="text-center py-2 bg-red-900/10 text-red-400 border-l border-gray-750">
                                                Matching SL Orders
                                            </th>
                                        </tr>
                                        <tr className="border-b border-gray-700/30">
                                            <th className="w-8 px-2 text-center">
                                                <input
                                                    type="checkbox"
                                                    className="w-4 h-4 rounded border-gray-700 bg-gray-900 text-blue-600 focus:ring-blue-500"
                                                    checked={filteredRows.some(r => r.matchingOrders?.length > 0) && filteredRows.every(r => !r.matchingOrders || r.matchingOrders.every(o => selectedOrderIds.has(o.order_id || o.orderId)))}
                                                    onChange={(e) => {
                                                        const allIds = new Set(selectedOrderIds);
                                                        filteredRows.forEach(r => {
                                                            if (r.matchingOrders) {
                                                                r.matchingOrders.forEach(o => {
                                                                    const id = o.order_id || o.orderId;
                                                                    if (e.target.checked) allIds.add(id);
                                                                    else allIds.delete(id);
                                                                });
                                                            }
                                                        });
                                                        setSelectedOrderIds(allIds);
                                                    }}
                                                />
                                            </th>
                                            <th className="py-2.5 px-3">
                                                <div className="flex items-center gap-1.5">
                                                    <span>Asset</span>
                                                    <select
                                                        value={selectedAsset}
                                                        onChange={(e) => setSelectedAsset(e.target.value)}
                                                        className="bg-[#1a1d29] border border-gray-700 text-gray-300 text-[10px] rounded px-1.5 py-0.5 focus:outline-none"
                                                    >
                                                        {uniqueAssets.map(a => <option key={a} value={a}>{a}</option>)}
                                                    </select>
                                                </div>
                                            </th>
                                            <th className="py-2.5 px-3">Symbol</th>
                                            <th className="py-2.5 px-3">Strategy</th>
                                            <th className="py-2.5 px-3 text-center">Strike</th>
                                            <th className="py-2.5 px-3 text-center">Base (1x)</th>
                                            <th className="py-2.5 px-3 text-center">Multiplier</th>
                                            <th className="py-2.5 px-3 text-center">Expected</th>
                                            <th className="py-2.5 px-3 text-center">Actual</th>
                                            <th className="py-2.5 px-3 text-right">Avg Entry</th>
                                            <th className="py-2.5 px-3 text-center border-r border-gray-700/50">Match</th>

                                            {/* Matching orders headers */}
                                            <th className="py-2.5 px-3 text-red-300 bg-red-900/5">Order ID</th>
                                            <th className="py-2.5 px-3 text-red-300 bg-red-900/5">Time</th>
                                            <th className="py-2.5 px-3 text-red-300 bg-red-900/5 text-center">Order Qty</th>
                                            <th className="py-2.5 px-3 text-red-300 bg-red-900/5 text-right">Stop Price</th>
                                            <th className="py-2.5 px-3 text-red-300 bg-red-900/5 text-right">Limit Price</th>
                                            <th className="py-2.5 px-3 text-red-300 bg-red-900/5 text-center">Total SL Qty</th>
                                            <th className="py-2.5 px-3 text-red-300 bg-red-900/5 text-center">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-800/40 text-gray-300">
                                        {filteredRows.map((row, idx) => {
                                            const hasOrders = row.matchingOrders && row.matchingOrders.length > 0;
                                            const isMatch = row.actualQty === row.expectedQty;
                                            const qtyColor = isMatch ? "text-green-400" : "text-red-400";

                                            return (
                                                <tr key={idx} className={`border-b border-gray-800/30 hover:bg-white/[0.01] transition-colors ${row.isOrphan ? "bg-yellow-950/5" : ""}`}>
                                                    {/* Checkboxes for SL orders */}
                                                    <td className="px-2 text-center align-middle">
                                                        {hasOrders && (
                                                            <div className="flex flex-col gap-1 items-center">
                                                                {row.matchingOrders.map((o, i) => (
                                                                    <div key={i} className="h-[20px] flex items-center">
                                                                        <input
                                                                            type="checkbox"
                                                                            className="w-3.5 h-3.5 rounded border-gray-850 bg-gray-950 text-blue-600 focus:ring-blue-500"
                                                                            checked={selectedOrderIds.has(o.order_id || o.orderId)}
                                                                            onChange={() => toggleOrderSelection(o.order_id || o.orderId)}
                                                                        />
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="py-2.5 px-3 text-blue-400 font-bold">{row.index}</td>
                                                    <td className="py-2.5 px-3 font-semibold text-white">
                                                        <div className="flex items-center gap-1.5">
                                                            <span>{row.symbol}</span>
                                                            {row.isOrphan && (
                                                                <span className="text-[8px] bg-yellow-500/10 text-yellow-500 px-1 py-px rounded font-bold">ORPHAN</span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="py-2.5 px-3 text-gray-400 font-semibold">{row.strategy}</td>
                                                    <td className="py-2.5 px-3 text-center font-mono">{row.strike}</td>
                                                    <td className="py-2.5 px-3 text-center font-mono">{row.isOrphan ? "-" : row.baseQty}</td>
                                                    <td className="py-2.5 px-3 text-center font-mono text-purple-400">{row.isOrphan ? "-" : `${row.multiplier}x`}</td>
                                                    <td className="py-2.5 px-3 text-center font-mono text-gray-400">{row.isOrphan ? "-" : row.expectedQty}</td>
                                                    <td className={`py-2.5 px-3 text-center font-mono font-bold ${qtyColor}`}>
                                                        {row.isOrphan ? "-" : (
                                                            <div className="flex items-center justify-center gap-1.5">
                                                                <span>{row.actualQty}</span>
                                                                {row.netSignedQty !== undefined && row.netSignedQty !== 0 && (
                                                                    <span className={`text-[9px] px-1.5 py-px rounded font-bold uppercase tracking-wide ${row.netSignedQty > 0 ? "bg-green-500/10 text-green-400" : "bg-red-500/10 text-red-400"}`}>
                                                                        {row.netSignedQty > 0 ? "L" : "S"}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="py-2.5 px-3 text-right font-mono">${formatNumber(row.avgEntry)}</td>
                                                    <td className="py-2.5 px-3 text-center border-r border-gray-700/50 text-sm">
                                                        {row.isOrphan ? (
                                                            <span className="text-yellow-500" title="Orphaned working SL order">⚠️</span>
                                                        ) : row.match ? (
                                                            <span className="text-green-400">✓</span>
                                                        ) : (
                                                            <span className="text-red-400">✗</span>
                                                        )}
                                                    </td>

                                                    {/* Mapped SL order columns */}
                                                    <td className="py-2.5 px-3 text-center font-mono bg-red-900/5 align-top">
                                                        {hasOrders ? (
                                                            <div className="flex flex-col gap-1.5">
                                                                {row.matchingOrders.map((o, i) => (
                                                                    <div key={i} className="text-[10px] text-gray-400 truncate max-w-[80px] h-[20px] flex items-center justify-center" title={o.order_id || o.orderId}>
                                                                        {o.order_id || o.orderId}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ) : "-"}
                                                    </td>
                                                    <td className="py-2.5 px-3 text-center font-mono text-gray-500 bg-red-900/5 align-top">
                                                        {hasOrders ? (
                                                            <div className="flex flex-col gap-1.5">
                                                                {row.matchingOrders.map((o, i) => (
                                                                    <div key={i} className="h-[20px] flex items-center justify-center">
                                                                        {formatOrderTime(o.orderdatetime || o.orderDateTime)}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ) : "-"}
                                                    </td>
                                                    <td className="py-2.5 px-3 text-center font-mono text-white bg-red-900/5 align-top">
                                                        {hasOrders ? (
                                                            <div className="flex flex-col gap-1.5">
                                                                {row.matchingOrders.map((o, i) => (
                                                                    <div key={i} className="h-[20px] flex items-center justify-center">
                                                                        {Math.abs(parseFloat(o.qty || 0))}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ) : "-"}
                                                    </td>
                                                    <td className="py-2.5 px-3 text-right font-mono text-gray-400 bg-red-900/5 align-top">
                                                        {hasOrders ? (
                                                            <div className="flex flex-col gap-1.5">
                                                                {row.matchingOrders.map((o, i) => (
                                                                    <div key={i} className="h-[20px] flex items-center justify-end">
                                                                        ${formatNumber(parseFloat(o.stopPrice || o.stop_price || 0))}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ) : "-"}
                                                    </td>
                                                    <td className="py-2.5 px-3 text-right font-mono text-gray-400 bg-red-900/5 align-top">
                                                        {hasOrders ? (
                                                            <div className="flex flex-col gap-1.5">
                                                                {row.matchingOrders.map((o, i) => (
                                                                    <div key={i} className="h-[20px] flex items-center justify-end">
                                                                        ${formatNumber(parseFloat(o.limitPrice || o.limit_price || 0))}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ) : "-"}
                                                    </td>
                                                    <td className="py-2.5 px-3 text-center font-mono font-bold text-red-400 bg-red-900/5 align-middle">
                                                        {row.totalSlQty}
                                                    </td>
                                                    <td className="py-2.5 px-3 text-center bg-red-900/5 align-top">
                                                        {hasOrders ? (
                                                            <div className="flex flex-col gap-1.5">
                                                                {row.matchingOrders.map((o, i) => (
                                                                    <div key={i} className="h-[20px] flex items-center justify-center gap-1">
                                                                        <button
                                                                            onClick={() => handleSingleModify(o)}
                                                                            className="p-1 text-blue-400 hover:text-white hover:bg-blue-600/20 border border-blue-800/50 rounded transition-all"
                                                                            title="Modify Order"
                                                                        >
                                                                            <Pencil className="w-3 h-3" />
                                                                        </button>
                                                                        <button
                                                                            onClick={() => handleCancelSingle(o)}
                                                                            className="px-2 py-0.5 rounded bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20 text-[9px] font-medium transition"
                                                                        >
                                                                            Cancel
                                                                        </button>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ) : "-"}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>

                {/* Confirm Cancel Modal */}
                {confirmModalOpen && (
                    <CancelConfirmationModal
                        isOpen={confirmModalOpen}
                        onClose={() => setConfirmModalOpen(false)}
                        onConfirm={handleConfirmCancel}
                        orders={ordersToCancel}
                        onDelete={handleRemoveFromBatch}
                    />
                )}

                {/* Exit Success Modal */}
                {successModalOpen && (
                    <AlpacaExitSuccessModal
                        isOpen={successModalOpen}
                        onClose={() => {
                            setSuccessModalOpen(false);
                            onClose();
                        }}
                        orders={submittedOrders}
                        actionType={successModalActionType}
                    />
                )}

                {/* Bulk Modify Modal */}
                {modifyModalOpen && (
                    <AlpacaBulkModifyModal
                        isOpen={modifyModalOpen}
                        onClose={() => setModifyModalOpen(false)}
                        orders={ordersToModify}
                        onSuccess={(orders) => {
                            setModifyModalOpen(false);
                            setSuccessModalActionType("Modify");
                            setSubmittedOrders(orders);
                            setSuccessModalOpen(true);
                        }}
                    />
                )}
            </div>
        </div>
    );
}
